import React, { useState } from 'react';
import { useQuery } from 'react-query';
import classnames from 'classnames';
import { Dropdown, Spinner } from 'react-bootstrap';
import restFactory from 'v2/utils/restFactory';
import styles from './DataProductRestrictions.module.scss';
import {
  DataProduct,
  DataProductRestrictionsResponse,
  DataProductRestriction,
} from 'v2/models';
import AddAssociationButton from 'v2/common/DRM/AddAssociationButton/AddAssociationButton';
import CreateDataProductRestrictionModal from './CreateDataProductRestrictionModal/CreateDataProductRestrictionModal';
import DeleteDataProductRestrictionModal from './DeleteDataProductRestrictionModal/DeleteDataProductRestrictionModal';
import EditDataProductRestrictionModal from './EditDataProductRestrictionModal/EditDataProductRestrictionModal';
import { TranslationKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface DataProductRestrictionsProps {
  dataProduct: DataProduct;
}

const DataProductRestrictions = ({
  dataProduct,
}: DataProductRestrictionsProps) => {
  const { t } = useTranslation();
  const [itemToRemove, setItemToRemove] =
    useState<DataProductRestriction | null>(null);
  const [itemToEdit, setItemToEdit] = useState<DataProductRestriction | null>(
    null
  );
  const [showCreateRestrictionModal, setShowCreateRestrictionModal] =
    useState(false);

  const canEdit = dataProduct.is_editable;

  const {
    data: restrictions,
    refetch,
    isLoading: isLoadingRestrictions,
  } = useQuery<DataProductRestrictionsResponse>(
    ['restrictions', dataProduct.id],
    async () => {
      return await restFactory.get<DataProductRestrictionsResponse>(
        `/dj/api/v1/restrictions/`,
        { data_product: dataProduct.id }
      );
    },
    { cacheTime: 0, refetchOnWindowFocus: false }
  );
  const restrictionsCount = restrictions?.count ?? 0;
  const hasRestrictions = restrictionsCount > 0;

  const reloadRestrictions = async () => {
    await refetch();
  };

  if (isLoadingRestrictions) {
    return (
      <div className={classnames(styles.loading)}>
        <Spinner role="status" animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className={styles.dataProductRestrictionContainer}>
        {canEdit && (
          <AddAssociationButton
            onClick={() => {
              setShowCreateRestrictionModal(true);
            }}
            label="Add a restriction"
            fitContentBtn={true}
          />
        )}
        {!hasRestrictions ? (
          <p className={classnames(styles.noData)}>
            This {t(TranslationKeys.dataProduct_one)} does not have any
            restrictions.
            {canEdit && ' Click above to add a restriction.'}
          </p>
        ) : (
          restrictions?.results.map((restriction, index) => {
            return (
              <div
                key={restriction.id}
                data-testid={`restriction_${restriction.id}`}
                data-cy="drm:dataset-restrictions"
              >
                <div className={classnames(styles.header, 'mb-3')}>
                  <div className={classnames(styles.title)}>
                    <span
                      data-testid={`header_${restriction.title.replaceAll(
                        ' ',
                        ''
                      )}`}
                    >
                      {`${index + 1}. ${restriction.title}`}
                    </span>
                  </div>
                  {canEdit && (
                    <Dropdown data-cy="restriction-item:restriction-actions-dropdown">
                      <Dropdown.Toggle
                        id="dropdown-autoclose-true"
                        variant="link"
                        className={classnames('p-0')}
                      >
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu data-cy="restriction-item:restriction-actions-options">
                        <Dropdown.Item
                          onClick={() => setItemToEdit(restriction)}
                        >
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setItemToRemove(restriction)}
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>

                <div data-testid={`value_Description`}>
                  <p className={styles.descriptionTexts}>
                    {restriction.restriction}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      {showCreateRestrictionModal && (
        <CreateDataProductRestrictionModal
          reloadRestrictions={reloadRestrictions}
          dataProduct={dataProduct}
          onClose={() => {
            setShowCreateRestrictionModal(false);
          }}
        />
      )}
      {itemToEdit && (
        <EditDataProductRestrictionModal
          reloadRestrictions={reloadRestrictions}
          dataProduct={dataProduct}
          itemToEdit={itemToEdit}
          onClose={() => {
            setItemToEdit(null);
          }}
        />
      )}
      {itemToRemove && (
        <DeleteDataProductRestrictionModal
          reloadRestrictions={reloadRestrictions}
          itemToRemove={itemToRemove}
          onClose={() => {
            setItemToRemove(null);
          }}
        />
      )}
    </>
  );
};

export default DataProductRestrictions;
